import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Search, BookOpen, ChevronRight, LifeBuoy } from 'lucide-react';
import { PAGES, AUDIENCES, SYMPTOMS, REFERENCED_FLAGS, bySlug } from '../help/content';
import { useFeatureFlags } from '../context/featureFlagStore';
import { useAuthStore } from '../context/authStore';

/**
 * Help, served by the portal rather than hosted somewhere else.
 *
 * Three things follow from it living here, and they are the reasons it does:
 * the page inherits the MSP's own branding, so their clients never read
 * HaloMTS's name; it knows who is reading, so an end customer is not shown the
 * administration pages; and it knows which modules are switched on, so nobody
 * reads instructions for a feature they do not have.
 */
/**
 * Turn a Markdown link into a route.
 *
 * Every "Related" link in docs/customer-docs points at a file —
 * `[Board routes](board-routes.md)` — because the Markdown has to work when
 * read on disk and on GitHub, and because the CI link check verifies those
 * files exist. Rendered unchanged, the browser resolves them against the
 * current URL and lands on a slug with `.md` on the end, which matches
 * nothing. Every one of them was dead, and the page blamed the reader's
 * permissions for it.
 *
 * Relative to the current page's directory, same as on disk: a bare name is a
 * sibling, `../` climbs.
 */
function resolveDocLink(href, fromSlug) {
  if (!href || /^(https?:|mailto:|#)/.test(href)) return null;
  const parts = fromSlug.split('/').slice(0, -1);
  for (const segment of href.replace(/\.md($|#)/, '$1').split('/')) {
    if (segment === '.' || segment === '') continue;
    if (segment === '..') parts.pop();
    else parts.push(segment);
  }
  return parts.join('/').split('#')[0];
}

export default function HelpPage() {
  const { '*': slug } = useParams();
  const navigate = useNavigate();
  const { user, hasMinRole } = useAuthStore();
  const flags = useFeatureFlags(REFERENCED_FLAGS);
  const [term, setTerm] = useState('');

  const isInternal = user?.isInternalStaff || hasMinRole('technician');

  const visible = useMemo(
    () =>
      PAGES.filter((p) => {
        // A page for a module that is switched off is worse than no page: it
        // describes a portal the reader is not using.
        if (p.flag && !flags[p.flag]) return false;
        // Customers are not shown how to administer the portal.
        // Platform-operator pages describe the console that manages every
        // tenant. They are not for an MSP's staff, however senior, so they
        // never appear inside a tenant portal's help centre at all.
        if (p.audience === 'platform') return false;
        if (!isInternal && p.audience !== 'customer') return false;
        return true;
      }),
    [flags, isInternal]
  );

  const page = slug ? bySlug(slug) : null;
  const allowed = page && visible.some((p) => p.slug === page.slug);

  const hits = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return [];
    const ok = new Set(visible.map((p) => p.slug));
    return SYMPTOMS.filter(
      (s) => ok.has(s.page.slug) && (s.symptom.toLowerCase().includes(t) || s.page.title.toLowerCase().includes(t))
    ).slice(0, 8);
  }, [term, visible]);

  const groups = useMemo(() => {
    const out = {};
    for (const p of visible) (out[p.audience] ||= []).push(p);
    return out;
  }, [visible]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900 dark:text-white">
            <LifeBuoy className="h-6 w-6 text-primary-600" /> Help
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Search by what you are seeing, not by what caused it.
          </p>
        </div>

        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            id="help-search"
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="e.g. payment declined"
            className="w-full rounded-lg border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm dark:border-neutral-600 dark:bg-neutral-700 dark:text-white"
          />
          {term.trim() && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
              {hits.length === 0 ? (
                <p className="px-3 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                  Nothing matches that. Raise a ticket and say what you typed — it is the quickest way for this page
                  to learn the words people actually use.
                </p>
              ) : (
                hits.map((h, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setTerm('');
                      navigate(`/help/${h.page.slug}`);
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700"
                  >
                    <span className="text-neutral-900 dark:text-white">{h.symptom}</span>
                    <span className="block text-xs text-neutral-500 dark:text-neutral-400">{h.page.title}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
        <nav className="space-y-6">
          {Object.entries(AUDIENCES).map(([id, meta]) =>
            groups[id]?.length ? (
              <div key={id}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  {meta.label}
                </p>
                <div className="space-y-0.5">
                  {groups[id].map((p) => (
                    <Link
                      key={p.slug}
                      to={`/help/${p.slug}`}
                      className={`block rounded px-2 py-1.5 text-sm leading-snug ${
                        page?.slug === p.slug
                          ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                          : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700'
                      }`}
                    >
                      {p.title}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </nav>

        <div className="min-w-0">
          {allowed ? (
            <article className="rounded-lg border border-neutral-200 bg-white p-6 dark:border-neutral-700 dark:bg-neutral-800 sm:p-8">
              <h2 className="mb-6 text-2xl font-bold text-neutral-900 dark:text-white">{page.title}</h2>
              <div className="help-prose">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a({ href, children, ...rest }) {
                      const target = resolveDocLink(href, page.slug);
                      if (!target) {
                        return (
                          <a href={href} target="_blank" rel="noreferrer" {...rest}>
                            {children}
                          </a>
                        );
                      }
                      // A page hidden because its module is off should not be
                      // offered and then refused. Keep the words, drop the link.
                      if (!visible.some((v) => v.slug === target)) return <span>{children}</span>;
                      return <Link to={`/help/${target}`}>{children}</Link>;
                    },
                  }}
                >
                  {page.body}
                </ReactMarkdown>
              </div>
            </article>
          ) : slug ? (
            <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center dark:border-neutral-700 dark:bg-neutral-800">
              {page ? (
                <>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    &ldquo;{page.title}&rdquo; is not available here.
                  </p>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    {page.flag
                      ? 'It covers a module that is switched off on this portal.'
                      : 'It is written for a different role.'}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-neutral-900 dark:text-white">There is no help page at that address.</p>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    The link may be out of date. Pick a page from the list, or search by what you are seeing.
                  </p>
                </>
              )}
              <Link to="/help" className="mt-3 inline-block text-sm text-primary-600 hover:underline dark:text-primary-400">
                All help pages
              </Link>
            </div>
          ) : (
            <Index groups={groups} />
          )}
        </div>
      </div>
    </div>
  );
}

/** The landing view: what is here, grouped the way the pages are written. */
function Index({ groups }) {
  return (
    <div className="space-y-6">
      {Object.entries(AUDIENCES).map(([id, meta]) =>
        groups[id]?.length ? (
          <section
            key={id}
            className="rounded-lg border border-neutral-200 bg-white p-6 dark:border-neutral-700 dark:bg-neutral-800"
          >
            <h2 className="flex items-center gap-2 font-semibold text-neutral-900 dark:text-white">
              <BookOpen className="h-4 w-4 text-primary-600" /> {meta.label}
            </h2>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{meta.blurb}</p>
            <div className="mt-4 grid gap-1 sm:grid-cols-2">
              {groups[id].map((p) => (
                <Link
                  key={p.slug}
                  to={`/help/${p.slug}`}
                  className="group flex items-center gap-1 rounded px-2 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400 group-hover:text-primary-600" />
                  {p.title}
                </Link>
              ))}
            </div>
          </section>
        ) : null
      )}
    </div>
  );
}
